import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Search, Check, ExternalLink, ChevronRight, MapPin, Sparkles, Layers, Cpu, FileText, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import { useCurrency, CURRENCIES } from '@/context/CurrencyContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/services/api';

const Header = ({ title, subtitle, breadcrumbs }) => {
  const { t } = useTranslation();
  const { currency, changeCurrency } = useCurrency();
  const navigate = useNavigate();
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [site, setSite] = useState('hq');
  const dropdownRef = useRef(null);

  // Global search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [bomResults, setBomResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  const doGlobalSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); setBomResults([]); setSearchOpen(false); return; }
    setSearchLoading(true);
    try {
      const [mpnRes, bomRes] = await Promise.allSettled([
        api.get(`/components/search/predictive?q=${encodeURIComponent(q)}`),
        api.get('/bom/list'),
      ]);
      const mpns = mpnRes.status === 'fulfilled' ? (mpnRes.value.data.mpns || []) : [];
      // Dedupe MPNs
      const seen = new Set();
      const unique = mpns.filter(m => { if (seen.has(m.mpn)) return false; seen.add(m.mpn); return true; });
      setSearchResults(unique.slice(0, 6));

      const boms = bomRes.status === 'fulfilled' ? (bomRes.value.data.boms || []) : [];
      const filtered = boms.filter(b => b.name?.toLowerCase().includes(q.toLowerCase())).slice(0, 3);
      setBomResults(filtered);
      setSearchOpen(true);
    } catch { /* ignore */ }
    finally { setSearchLoading(false); }
  }, []);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => doGlobalSearch(val), 300);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      setSearchOpen(false);
      navigate(`/component-intelligence?mpn=${encodeURIComponent(searchQuery.trim())}`);
    }
    if (e.key === 'Escape') setSearchOpen(false);
  };

  const selectMpn = (mpn) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/component-intelligence/${mpn}`);
  };

  const selectBom = (bomId) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/bom-intelligence/${bomId}`);
  };

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/actions/notifications');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      setUnreadCount(0);
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/actions/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotifClick = async (notif) => {
    try { await api.put(`/actions/notifications/${notif.notification_id}/read`); } catch {}
    setShowNotifs(false);
    navigate('/actions');
  };

  const typeIcon = (type) => {
    if (type?.includes('completed')) return '✅';
    if (type?.includes('escalated')) return '⚠️';
    if (type?.includes('blocked')) return '🚫';
    if (type?.includes('reassigned')) return '🔄';
    return '📋';
  };

  const recent5 = notifications.slice(0, 5);

  const sites = [
    { value: 'hq', label: 'Head Office' },
    { value: 'plant_1', label: 'Plant 1 - Shanghai' },
    { value: 'plant_2', label: 'Plant 2 - Shenzhen' },
    { value: 'office_eu', label: 'EU Office - Munich' },
  ];

  const platforms = [
    { id: '1data', label: '1Data', active: true },
    { id: '1source', label: '1Source', active: false },
    { id: '1xcess', label: '1Xcess', active: false },
  ];

  return (
    <div className="bg-white border-b border-gray-200 px-8 py-4 shadow-sm">
      {/* Breadcrumbs row */}
      <div className="flex items-center justify-between mb-1">
        <nav className="flex items-center text-xs text-gray-400" data-testid="breadcrumbs">
          <Link to="/dashboard" className="hover:text-green-600 transition-colors">1Data</Link>
          {breadcrumbs ? (
            breadcrumbs.map((bc, i) => (
              <React.Fragment key={i}>
                <ChevronRight className="h-3 w-3 mx-1" />
                {bc.path ? (
                  <Link to={bc.path} className="hover:text-green-600 transition-colors">{bc.label}</Link>
                ) : (
                  <span className="text-gray-600 font-medium">{bc.label}</span>
                )}
              </React.Fragment>
            ))
          ) : title ? (
            <>
              <ChevronRight className="h-3 w-3 mx-1" />
              <span className="text-gray-600 font-medium">{title}</span>
            </>
          ) : null}
        </nav>
        <div className="flex items-center gap-2">
          {/* Platform Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1.5 text-gray-500" data-testid="platform-switcher">
                <Layers className="h-3.5 w-3.5" />
                <span className="font-semibold text-green-700">1Data</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {platforms.map(p => (
                <DropdownMenuItem key={p.id} className={p.active ? 'bg-green-50 text-green-700 font-medium' : ''} data-testid={`platform-${p.id}`}>
                  {p.active && <Check className="h-3 w-3 mr-1.5" />}
                  {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Site Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1.5 text-gray-500" data-testid="site-switcher">
                <MapPin className="h-3.5 w-3.5" />
                <span>{sites.find(s => s.value === site)?.label || 'Head Office'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {sites.map(s => (
                <DropdownMenuItem key={s.value} onClick={() => setSite(s.value)} className={site === s.value ? 'bg-green-50 text-green-700 font-medium' : ''} data-testid={`site-${s.value}`}>
                  {site === s.value && <Check className="h-3 w-3 mr-1.5" />}
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="page-title">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          <Select value={currency} onValueChange={changeCurrency}>
            <SelectTrigger className="w-24 h-9 text-xs" data-testid="currency-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => (
                <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <LanguageSelector />

          <div className="relative w-72" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('common.search') + ' components, BOMs...'}
              className="pl-10 bg-gray-50 border-gray-300 text-gray-900 focus:border-green-500 h-9 text-sm"
              data-testid="header-search"
              value={searchQuery}
              onChange={handleSearchInput}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (searchQuery.length >= 2) setSearchOpen(true); }}
            />
            {searchOpen && (searchResults.length > 0 || bomResults.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl z-50 overflow-hidden max-h-96 overflow-y-auto" data-testid="global-search-dropdown">
                {searchResults.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b">Components</div>
                    {searchResults.map((r, i) => (
                      <div key={`${r.mpn}-${i}`} className="flex items-center gap-3 px-3 py-2 hover:bg-green-50 cursor-pointer transition-colors border-b border-gray-50" onClick={() => selectMpn(r.mpn)} data-testid={`search-result-mpn-${i}`}>
                        <Cpu className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.mpn}</p>
                          <p className="text-[10px] text-gray-400 truncate">{r.manufacturer} · {r.category}</p>
                        </div>
                        <ArrowRight className="h-3 w-3 text-gray-300 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
                {bomResults.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b">BOMs</div>
                    {bomResults.map((b, i) => (
                      <div key={b.bom_id} className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-50" onClick={() => selectBom(b.bom_id)} data-testid={`search-result-bom-${i}`}>
                        <FileText className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{b.name}</p>
                          <p className="text-[10px] text-gray-400">{b.total_components} components · {b.status}</p>
                        </div>
                        <ArrowRight className="h-3 w-3 text-gray-300 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
                {searchQuery.trim() && (
                  <div className="px-3 py-2 bg-gray-50 border-t cursor-pointer hover:bg-green-50 transition-colors" onClick={() => { setSearchOpen(false); navigate(`/component-intelligence?mpn=${encodeURIComponent(searchQuery.trim())}`); }} data-testid="search-view-all">
                    <p className="text-xs text-green-700 font-medium flex items-center gap-1"><Search className="h-3 w-3" /> Search "{searchQuery}" in Part Intelligence</p>
                  </div>
                )}
              </div>
            )}
            {searchOpen && searchLoading && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 px-4 py-3 text-xs text-gray-400">
                Searching...
              </div>
            )}
          </div>

          {/* Notification Bell */}
          <div className="relative" ref={dropdownRef}>
            <Button variant="ghost" size="icon" className="relative text-gray-600 hover:text-gray-900 hover:bg-gray-100" data-testid="notifications-button" onClick={() => setShowNotifs(!showNotifs)}>
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-[10px]" data-testid="notification-count">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </Button>
            {showNotifs && (
              <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
                <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs text-blue-600 h-7" onClick={markAllRead} data-testid="mark-all-read">
                      <Check className="h-3 w-3 mr-1" /> Mark all as read
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {recent5.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 text-sm">No notifications yet</div>
                  ) : (
                    recent5.map(n => (
                      <div key={n.notification_id} className={`px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${!n.is_read ? 'bg-blue-50/50' : ''}`} onClick={() => handleNotifClick(n)} data-testid={`notif-item-${n.notification_id}`}>
                        <div className="flex items-start gap-3">
                          <span className="text-base flex-shrink-0 mt-0.5">{typeIcon(n.type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{n.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{n.body}</p>
                            <p className="text-[10px] text-gray-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                          </div>
                          {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="px-4 py-2 border-t bg-gray-50 text-center">
                  <Button variant="ghost" size="sm" className="text-xs text-green-700 w-full" onClick={() => { setShowNotifs(false); navigate('/actions'); }} data-testid="view-all-notifications">
                    <ExternalLink className="h-3 w-3 mr-1" /> View All in Action Center
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
